/**
 * Auth hook for operator identity.
 *
 * Provides access to the current operator's identity via the API client.
 */

import { create } from 'zustand'
import { getOperatorIdentity } from '../api'
import type { OperatorInfo } from '../types'

interface AuthState {
  /** Current operator info, null if not authenticated */
  operator: OperatorInfo | null
  /** Whether auth check is in progress */
  isLoading: boolean
  /** Error message if auth failed */
  error: string | null
  /** Whether initial auth check has completed */
  hasChecked: boolean

  /** Check authentication status and fetch operator identity */
  checkAuth: () => Promise<void>
  /** Clear auth state (for logout/error recovery) */
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  operator: null,
  isLoading: false,
  error: null,
  hasChecked: false,

  checkAuth: async () => {
    // Avoid duplicate checks
    if (get().isLoading) return

    set({ isLoading: true, error: null })

    try {
      const operator = await getOperatorIdentity()
      set({
        operator,
        isLoading: false,
        hasChecked: true,
      })
    } catch (err) {
      set({
        operator: null,
        isLoading: false,
        hasChecked: true,
        error: err instanceof Error ? err.message : String(err),
      })
    }
  },

  clearAuth: () => {
    set({
      operator: null,
      isLoading: false,
      error: null,
      hasChecked: false,
    })
  },
}))

/**
 * Hook to get current operator info.
 *
 * Returns the operator if authenticated, null otherwise.
 */
export function useOperator(): OperatorInfo | null {
  return useAuthStore((state) => state.operator)
}

/**
 * Hook to check if auth is loading.
 */
export function useAuthLoading(): boolean {
  return useAuthStore((state) => state.isLoading)
}

/**
 * Hook to get auth error.
 */
export function useAuthError(): string | null {
  return useAuthStore((state) => state.error)
}

/**
 * Hook to check if initial auth check has completed.
 */
export function useAuthChecked(): boolean {
  return useAuthStore((state) => state.hasChecked)
}
